import {
  type BranchKey,
  configurationKeys,
  useSquashBranch,
} from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { typeUtils } from '@festina-life/core'
import { toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikTextArea } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Popover, PopoverButton } from '@festina-life/ui/structure'
import { useQueryClient } from '@tanstack/react-query'
import * as Yup from 'yup'

import { useBaseBranchWorkspaces } from '../../../../hooks/useBaseBranchWorkspaces'
import { isEqualBranch } from '../../../../uiModels/branches/helpers'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { ActionsMenuPanel } from '../../../actionsPanel/ActionsMenuPanel'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { useRebaseWorkspace } from '../../../workspacesPanel/workspaceDetails/manageWorkspace/useRebaseWorkspace'

type SquashInput = {
  description: string
}

const validate = typeUtils.createValidator<SquashInput>()

type Props = {
  branchKey: BranchKey
}

export const SquashBranchButton = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const { mutateAsync: squashBranch, isPending } = useSquashBranch()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const queryClient = useQueryClient()
  const invalidateBranch = useInvalidateBranchAfterHistoryRewrite()
  const rebaseWorkspace = useRebaseWorkspace()
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const singleWorkspace =
    branchWorkspaces.length === 1 ? branchWorkspaces[0] : undefined

  const validationsSchema = Yup.object({
    description: Yup.string().optional(),
  })

  const handleSquashBranch = async (value: SquashInput, close: () => void) => {
    try {
      await squashBranch({
        branchName: branchKey.name,
        server: branchKey.server,
        description: value.description,
      })

      const isBranchWorkspaceBaseBranch =
        currentWorkspaceKey &&
        isEqualBranch(currentWorkspaceKey.branchKey, branchKey)

      if (isBranchWorkspaceBaseBranch) {
        await queryClient.invalidateQueries({
          queryKey: configurationKeys.fetchWorkspaceBaseBranchChangesetsKey({
            workspaceName: currentWorkspaceKey.name,
            server: currentWorkspaceKey.branchKey.server,
          }),
        })
      }

      invalidateBranch(branchKey)

      if (singleWorkspace) {
        const workspaceKey = {
          name: singleWorkspace.name ?? '',
          branchKey,
        }
        await rebaseWorkspace(workspaceKey)
      }

      toast({
        variant: 'success',
        title: translate('branchesTree.toast.squashed', {
          branchName: branchKey.name,
        }),
      })
      close()
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    }
  }

  return (
    <Popover>
      {({ close }) => (
        <>
          <PopoverButton as="div">
            <OperationsLoadedButton
              startIcon="ArrowsPointingIn"
              isLoading={isPending}
            >
              {translate('branchDetailsPanel.tabs.manage.squash')}
            </OperationsLoadedButton>
          </PopoverButton>
          <ActionsMenuPanel anchor={{ to: 'right' }} className="max-w-lg!">
            <FormikForm
              initialValues={{ description: '' }}
              onSubmit={(value) => handleSquashBranch(value, close)}
              validationSchema={validationsSchema}
            >
              <FormikTextArea
                name={validate('description')}
                label={translate(
                  'branchDetailsPanel.tabs.manage.squash.descriptionLabel',
                )}
                description={translate(
                  'branchDetailsPanel.tabs.manage.squash.descriptionHelp',
                )}
              />

              <div className="flex w-full justify-end">
                <OperationsLoadedButton
                  type="submit"
                  variant="primary"
                  isLoading={isPending}
                >
                  {translate('branchDetailsPanel.tabs.manage.squash')}
                </OperationsLoadedButton>
              </div>
            </FormikForm>
          </ActionsMenuPanel>
        </>
      )}
    </Popover>
  )
}
